import React, { memo } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Class, Subject } from '../../types';
import type { PaperNumbering } from '../../services/generateQuestionPaperService';
import {
  NUMBERING_STYLES,
  NUMBERING_STYLE_LABELS,
  MARKS_DISPLAY_MODES,
  MARKS_DISPLAY_MODE_LABELS,
  MCQ_OPTION_STYLES,
  MCQ_OPTION_STYLE_LABELS,
  MarksDisplayMode,
  MCQOptionStyle,
  NumberingStyle,
} from '../../utils/paperNumbering';
import { elegantMenuProps } from '../../utils/menuStyles';

interface PaperFormData {
  classId: string;
  subjectId: string;
  title: string;
  description: string;
  instructions: string;
  totalMarks: number;
  duration: number;
  /** ISO date string (YYYY-MM-DD) for the scheduled exam. Empty = no date
   *  printed; falls back to a blank Date line teachers can fill by hand. */
  examDate: string;
  textDirection: 'ltr' | 'rtl';
  marksDisplayMode: MarksDisplayMode;
  numbering: PaperNumbering;
}

interface PaperDetailsStepProps {
  paperForm: PaperFormData;
  onUpdateForm: (updates: Partial<PaperFormData>) => void;
  classes: Class[];
  subjects: Subject[];
  onFetchSubjects: (classId: string) => void;
  isViewMode: boolean;
  currentMarks: number;
  totalMarksExceeded: boolean;
}

const PaperDetailsStep = memo<PaperDetailsStepProps>(({
  paperForm,
  onUpdateForm,
  classes,
  subjects,
  onFetchSubjects,
  isViewMode,
  currentMarks,
  totalMarksExceeded,
}) => {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Basic Information
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required disabled={isViewMode}>
            <InputLabel>Class</InputLabel>
            <Select MenuProps={elegantMenuProps}
              value={paperForm.classId}
              label="Class"
              onChange={(e) => {
                const newClassId = e.target.value;
                onUpdateForm({ classId: newClassId, subjectId: '' });
                onFetchSubjects(newClassId);
              }}
              disabled={isViewMode}
            >
              {classes.map((cls) => (
                <MenuItem key={cls._id} value={cls._id}>{cls.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required disabled={!paperForm.classId || isViewMode}>
            <InputLabel>Subject</InputLabel>
            <Select MenuProps={elegantMenuProps}
              value={paperForm.subjectId}
              label="Subject"
              onChange={(e) => onUpdateForm({ subjectId: e.target.value })}
              disabled={isViewMode}
            >
              {subjects.map((subject) => (
                <MenuItem key={subject._id} value={subject._id}>{subject.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Title"
            required
            value={paperForm.title}
            onChange={(e) => onUpdateForm({ title: e.target.value })}
            placeholder="e.g., Mid-Term Examination 2024"
            disabled={isViewMode}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Description (Optional)"
            multiline
            rows={2}
            value={paperForm.description}
            onChange={(e) => onUpdateForm({ description: e.target.value })}
            placeholder="Brief description of this question paper"
            disabled={isViewMode}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Instructions (Optional)"
            multiline
            rows={3}
            value={paperForm.instructions}
            onChange={(e) => onUpdateForm({ instructions: e.target.value })}
            placeholder="e.g., Read all questions carefully. All questions are compulsory..."
            disabled={isViewMode}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Paper Configuration
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="number"
            label="Duration (minutes)"
            required
            value={paperForm.duration}
            onChange={(e) => onUpdateForm({ duration: parseInt(e.target.value) || 0 })}
            inputProps={{ min: 0 }}
            disabled={isViewMode}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="number"
            label="Total Marks"
            value={paperForm.totalMarks}
            onChange={(e) => onUpdateForm({ totalMarks: parseInt(e.target.value) || 0 })}
            inputProps={{ min: 0 }}
            helperText="Set to 0 for auto-calculation"
            disabled={isViewMode}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label="Current Marks"
            value={`${currentMarks}${paperForm.totalMarks > 0 ? ` / ${paperForm.totalMarks}` : ''}`}
            InputProps={{ readOnly: true }}
            error={totalMarksExceeded}
            helperText={totalMarksExceeded ? 'Exceeds total marks' : ''}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            type="date"
            label="Exam Date (Optional)"
            value={paperForm.examDate}
            onChange={(e) => onUpdateForm({ examDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            disabled={isViewMode}
            helperText="Printed on the PDF header"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth size="small" disabled={isViewMode}>
            <InputLabel>Text Direction</InputLabel>
            <Select MenuProps={elegantMenuProps}
              value={paperForm.textDirection}
              label="Text Direction"
              onChange={(e) => onUpdateForm({ textDirection: e.target.value as 'ltr' | 'rtl' })}
              disabled={isViewMode}
            >
              <MenuItem value="ltr">Left to Right (LTR)</MenuItem>
              <MenuItem value="rtl">Right to Left (RTL)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/*
        Numbering & marks display — collapsed by default so novices ignore it,
        but every board admin who wants "Q3 OR Q4" or "SECTION A" style
        control can dial it in without touching config or code.
      */}
      <Accordion
        elevation={0}
        disableGutters
        sx={{
          mt: 4,
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 2,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Numbering & Display</Typography>
            <Typography variant="caption" color="text.secondary">
              How sections, questions, and sub-parts are labelled; where marks appear.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small" disabled={isViewMode}>
                <InputLabel>Question numbers</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={paperForm.numbering.questionStyle}
                  label="Question numbers"
                  onChange={(e) =>
                    onUpdateForm({
                      numbering: { ...paperForm.numbering, questionStyle: e.target.value as NumberingStyle },
                    })
                  }
                >
                  {NUMBERING_STYLES.map((s) => (
                    <MenuItem key={s} value={s}>{NUMBERING_STYLE_LABELS[s]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small" disabled={isViewMode}>
                <InputLabel>Sub-part numbers</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={paperForm.numbering.subPartStyle}
                  label="Sub-part numbers"
                  onChange={(e) =>
                    onUpdateForm({
                      numbering: { ...paperForm.numbering, subPartStyle: e.target.value as NumberingStyle },
                    })
                  }
                >
                  {NUMBERING_STYLES.map((s) => (
                    <MenuItem key={s} value={s}>{NUMBERING_STYLE_LABELS[s]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small" disabled={isViewMode}>
                <InputLabel>MCQ options</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={paperForm.numbering.mcqOptionStyle}
                  label="MCQ options"
                  onChange={(e) =>
                    onUpdateForm({
                      numbering: { ...paperForm.numbering, mcqOptionStyle: e.target.value as MCQOptionStyle },
                    })
                  }
                >
                  {MCQ_OPTION_STYLES.map((s) => (
                    <MenuItem key={s} value={s}>{MCQ_OPTION_STYLE_LABELS[s]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small" disabled={isViewMode}>
                <InputLabel>Show marks as</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={paperForm.marksDisplayMode}
                  label="Show marks as"
                  onChange={(e) => onUpdateForm({ marksDisplayMode: e.target.value as MarksDisplayMode })}
                >
                  {MARKS_DISPLAY_MODES.map((m) => (
                    <MenuItem key={m} value={m}>{MARKS_DISPLAY_MODE_LABELS[m]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
});

PaperDetailsStep.displayName = 'PaperDetailsStep';

export default PaperDetailsStep;
